import React, { useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Skeleton,
  Box,
  Alert
} from '@mui/material';
import { useEndReasonTypeData } from '../contexts/EndReasonTypeDataContext';
import { EndReasonTypeFilterControls } from './EndReasonTypeFilterControls';
import { DashboardCard } from '../components/DashboardCard';

const EndReasonTypeTableContent = () => {
  const { data, loading, error, totalsByYear, filter } = useEndReasonTypeData();
  const { years, endReasonTypes } = useMemo(() => {
    if (!data) return { years: [], endReasonTypes: [] };
    
    return {
      years: [...new Set(data.map((d) => d.year))].sort(),
      endReasonTypes: [...new Set(data.map((d) => d.endReasonTypeName))].sort()
    };
  }, [data]);

  if (loading) return <Skeleton variant="rounded" height={300} />;
  
  if (error) return <Alert severity="error">{error}</Alert>;

  if (!data || data.length === 0) {
    return (
      <Typography align="center" color="textSecondary" sx={{ py: 5 }}>
        No end reason type data available for selected years
      </Typography>
    );
  }

  if (filter.schoolYears.length === 1) {
    const selectedYear = filter.schoolYears[0];
    const yearData = data.filter((d) => d.year === selectedYear);
    const yearTotal = totalsByYear.find(t => t.year === selectedYear);

    return (
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>End Reason Type</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>Number of Students</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>Number Enrolled</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>% Submitted</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {yearData.map((item) => (
              <TableRow key={item.endReasonTypeId}>
                <TableCell>{item.endReasonTypeName}</TableCell>
                <TableCell align="right">{item.studentCount}</TableCell>
                <TableCell align="right">{item.totalEnrolled}</TableCell>
                <TableCell align="right">
                  <strong>{item.percentageSubmitted.toFixed(1)}%</strong>
                </TableCell>
              </TableRow>
            ))}
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell sx={{ fontWeight: 'bold' }}>Total</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>{yearTotal?.totalStudents ?? 0}</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>{yearTotal?.totalEnrolled ?? 0}</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                {(yearTotal?.totalPercentage ?? 0).toFixed(1)}%
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  return (
    <TableContainer component={Paper}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 'bold', minWidth: 200 }}>End Reason Type</TableCell>
            {years.map((year) => (
              <TableCell key={year} align="center" sx={{ fontWeight: 'bold' }}>
                {`${year}-${year + 1}`}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {endReasonTypes.map((endReasonType) => (
            <TableRow key={endReasonType}>
              <TableCell>{endReasonType}</TableCell>
              {years.map((year) => {
                const cellData = data.find((d) => d.year === year && d.endReasonTypeName === endReasonType);
                if (!cellData) return <TableCell key={year} align="center">-</TableCell>;
                return (
                  <TableCell key={year} align="center">
                    {`${cellData.studentCount} (${cellData.percentageSubmitted.toFixed(1)}%)`}
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
          <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
            <TableCell sx={{ fontWeight: 'bold' }}>Total</TableCell>
            {years.map((year) => {
              const yearTotal = totalsByYear.find((t) => t.year === year);
              if (!yearTotal) return <TableCell key={year} align="center" sx={{ fontWeight: 'bold' }}>-</TableCell>;
              return (
                <TableCell key={year} align="center" sx={{ fontWeight: 'bold' }}>
                  {`${yearTotal.totalStudents} (${(yearTotal.totalPercentage ?? 0).toFixed(1)}%)`}
                </TableCell>
              );
            })}
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export const EndReasonTypeTableCard = () => {
  return (
    <DashboardCard title="End Reason Type">
      <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', height: '100%' }}>
        <EndReasonTypeFilterControls />
        <Box sx={{ flexGrow: 1, minHeight: 0, mt: 2 }}>
          <EndReasonTypeTableContent />
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
          Note: Percentages based on total enrolled students per year
        </Typography>
      </Box>
    </DashboardCard>
  );
};